import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

/**
 * Kullanıcı arayüz tercihleri — renk modu MUI `useColorScheme` tarafından ayrıca
 * yönetilir (kendi `localStorage` anahtarıyla); burada yalnız sidebar daraltma ve
 * içerik yoğunluğu tutulur. Yoğunluk primitive'lerce doğrudan okunur — global tema
 * yeniden yaratılmaz.
 *
 * Önceki sürümün `localStorage['tfw:sidebarCollapsed']` ('1'/'0') anahtarını ilk
 * yüklemede taşır; daha yeni bir `tfw:ui` değeri varsa onu ezmez.
 */
export type Density = 'comfortable' | 'compact';

const LEGACY_SIDEBAR_KEY = 'tfw:sidebarCollapsed';
const STORAGE_KEY = 'tfw:ui';

interface UiPrefsState {
  sidebarCollapsed: boolean;
  density: Density;
  setSidebarCollapsed: (v: boolean) => void;
  toggleSidebar: () => void;
  setDensity: (d: Density) => void;
}

function readLegacySidebarCollapsed(): boolean {
  try {
    return window.localStorage.getItem(LEGACY_SIDEBAR_KEY) === '1';
  } catch {
    return false;
  }
}

function hasPersistedPrefs(): boolean {
  try {
    return window.localStorage.getItem(STORAGE_KEY) != null;
  } catch {
    return false;
  }
}

export const useUiPrefs = create<UiPrefsState>()(
  persist(
    (set) => ({
      // Yeni kalıcı değer yoksa legacy anahtardan tohumla.
      sidebarCollapsed: hasPersistedPrefs() ? false : readLegacySidebarCollapsed(),
      density: 'comfortable',
      setSidebarCollapsed: (sidebarCollapsed) => set({ sidebarCollapsed }),
      toggleSidebar: () => set((s) => ({ sidebarCollapsed: !s.sidebarCollapsed })),
      setDensity: (density) => set({ density }),
    }),
    {
      name: STORAGE_KEY,
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        sidebarCollapsed: state.sidebarCollapsed,
        density: state.density,
      }),
      onRehydrateStorage: () => () => {
        // Legacy anahtarı temizle — artık `tfw:ui` yetkili.
        try {
          window.localStorage.removeItem(LEGACY_SIDEBAR_KEY);
        } catch {
          /* yok say */
        }
      },
    }
  )
);
